import { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Award, 
  Users, 
  Clock, 
  HeartHandshake, 
  Target,
  Droplets,
  ArrowRight,
  Phone
} from 'lucide-react';

const POOL_IMAGE = "https://images.unsplash.com/photo-1771218830084-fdd272e149a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2wlMjBzdW5zZXQlMjBtZWRpdGVycmFuZWFuJTIwdmlsbGElMjBvY2VhbiUyMHZpZXd8ZW58MHx8fHwxNzczMzE4ODI3fDA&ixlib=rb-4.1.0&q=85";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_ea404883-7061-4aba-83d0-2d4ab5ad0223/artifacts/ffswoxpp_69d6c27f-288c-44ba-8ef2-7664150a7535.jpeg";

const values = [
  { icon: HeartHandshake, title: 'Compromiso', description: 'Tu satisfacción es nuestra prioridad.' },
  { icon: Award, title: 'Calidad', description: 'Solo los mejores materiales.' },
  { icon: Users, title: 'Cercanía', description: 'Trato personalizado y familiar.' },
  { icon: Target, title: 'Profesionalidad', description: 'Equipo cualificado y formado.' },
];

const timeline = [
  { year: '2008', event: 'Fundación' },
  { year: '2012', event: 'Expansión Costa Brava' },
  { year: '2018', event: '+300 piscinas' },
  { year: '2024', event: 'Líderes Alt Empordà' },
];

// 3D Value Card
const ValueCard3D = ({ value, index }) => {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'end start'],
  });

  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [index % 2 === 0 ? -20 : 20, 0, index % 2 === 0 ? 10 : -10]);
  const y = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [60, 0, 0, -30]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.9, 1, 1, 0.95]);

  const ySpring = useSpring(y, { stiffness: 80, damping: 20 });
  const Icon = value.icon;

  return (
    <motion.div
      ref={cardRef}
      style={{ rotateY, y: ySpring, scale, transformStyle: 'preserve-3d' }}
      className="group bg-white p-5 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl shadow-sm hover:shadow-xl transition-all"
      data-testid={`value-${value.title.toLowerCase()}`}
    >
      <motion.div 
        className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-sky-100 flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-sky-500 transition-colors"
        whileHover={{ scale: 1.1, rotate: 5 }}
      >
        <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-sky-500 group-hover:text-white transition-colors" />
      </motion.div>
      <h3 className="font-manrope font-bold text-lg sm:text-xl text-slate-900 mb-2 sm:mb-3">
        {value.title}
      </h3>
      <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
        {value.description}
      </p>
    </motion.div>
  );
};

export const AboutPage = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  const heroY = useTransform(scrollYProgress, [0, 0.2], [0, -30]);
  const imageY = useTransform(scrollYProgress, [0, 0.5], [0, 50]);
  const imageRotate = useTransform(scrollYProgress, [0, 0.5], [0, 5]);

  return (
    <main ref={containerRef} className="pt-16 sm:pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-sky-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 right-10 w-48 sm:w-72 h-48 sm:h-72 bg-sky-100 rounded-full blur-3xl opacity-50" />
          <div className="absolute bottom-10 left-10 w-64 sm:w-96 h-64 sm:h-96 bg-cyan-100 rounded-full blur-3xl opacity-40" />
        </div>

        <motion.div 
          style={{ y: heroY }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center"
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-sky-100 text-sky-600 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6"
          >
            <Droplets className="w-3 h-3 sm:w-4 sm:h-4" />
            Sobre Nosotros
          </motion.span>

          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-manrope font-extrabold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-slate-900 mb-4 sm:mb-6 tracking-tight px-4"
          >
            Pasión por el{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-cyan-500">
              agua
            </span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed px-4"
          >
            Más de 15 años haciendo realidad el sueño de tener una piscina perfecta en la Costa Brava.
          </motion.p>
        </motion.div>
      </section>

      {/* Story */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Image */}
            <motion.div
              style={{ y: imageY, rotate: imageRotate }}
              className="relative"
            >
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={POOL_IMAGE}
                  alt="Piscina de lujo"
                  className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-sky-900/30 to-transparent" />
              </div>
              
              {/* Logo badge */}
              <motion.div 
                className="absolute -bottom-4 sm:-bottom-6 lg:-bottom-8 -right-2 sm:-right-4 lg:-right-8 bg-white p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl shadow-xl"
                whileHover={{ scale: 1.05, rotate: 5 }}
              >
                <img 
                  src={LOGO_URL}
                  alt="Logo"
                  className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 object-contain"
                />
              </motion.div>
            </motion.div>

            {/* Content */}
            <div className="px-2 sm:px-0">
              <h2 className="font-manrope font-extrabold text-2xl sm:text-3xl lg:text-4xl text-slate-900 mb-4 sm:mb-6">
                Nuestra historia
              </h2>
              
              <div className="space-y-3 sm:space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed mb-6 sm:mb-8">
                <p>
                  <strong className="text-slate-800">Piscinas Genovés</strong> nació en 2008 de la mano de 
                  una familia apasionada por el agua y el servicio al cliente.
                </p>
                <p>
                  Nuestra filosofía: <strong className="text-slate-800">calidad, compromiso y cercanía</strong>.
                </p>
              </div>

              <div className="flex items-center gap-4 sm:gap-6">
                {[
                  { number: '+15', label: 'Años' },
                  { number: '500+', label: 'Proyectos' },
                  { number: '100%', label: 'Dedicación' },
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="font-manrope font-extrabold text-2xl sm:text-3xl lg:text-4xl text-sky-500">{stat.number}</div>
                    <div className="text-xs sm:text-sm text-slate-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-12 sm:py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <span className="inline-flex items-center gap-2 bg-white text-slate-700 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6 shadow-sm border border-slate-100">
              <Award className="w-3 h-3 sm:w-4 sm:h-4 text-sky-500" />
              Nuestros valores
            </span>
            <h2 className="font-manrope font-extrabold text-2xl sm:text-3xl lg:text-4xl text-slate-900 mb-2 sm:mb-4">
              Lo que nos define
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {values.map((value, index) => (
              <ValueCard3D key={value.title} value={value} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <span className="inline-flex items-center gap-2 bg-sky-100 text-sky-600 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
              Trayectoria
            </span>
            <h2 className="font-manrope font-extrabold text-2xl sm:text-3xl lg:text-4xl text-slate-900">
              Hitos importantes
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-sky-100 -translate-x-1/2" />
            
            {timeline.map((item, index) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative flex items-center gap-4 sm:gap-8 mb-8 sm:mb-12 ${
                  index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                }`}
              >
                <div className={`flex-1 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                  <div className="font-manrope font-extrabold text-xl sm:text-2xl text-sky-500 mb-1">
                    {item.year}
                  </div>
                  <div className="text-slate-600 text-sm sm:text-base">{item.event}</div>
                </div>
                <div className="absolute left-1/2 -translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-sky-500 border-4 border-white shadow-md" />
                <div className="flex-1" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 lg:py-20 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-sky-500/10"
              style={{
                width: 20 + Math.random() * 50,
                height: 20 + Math.random() * 50,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{ y: [0, -20, 0], scale: [1, 1.2, 1] }}
              transition={{ duration: 4 + Math.random() * 2, repeat: Infinity, delay: i * 0.4 }}
            />
          ))}
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-manrope font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white mb-4 sm:mb-6">
            ¿Quieres conocernos?
          </h2>
          <p className="text-base sm:text-lg text-slate-400 mb-6 sm:mb-8">
            Descubre cómo podemos ayudarte con tu proyecto.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5">
            <Link
              to="/contacto"
              data-testid="about-cta"
              className="w-full sm:w-auto group flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-400 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-sm sm:text-base transition-all hover:scale-105 shadow-lg"
            >
              Contactar
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="tel:+34634060878"
              className="flex items-center gap-2 text-sky-400 hover:text-sky-300 font-medium text-sm sm:text-base"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
              +34 634 06 08 78
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
